import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import {
  Container,
  Typography,
  Paper,
  Box,
  Checkbox,
  FormControlLabel,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';

  const db = getFirestore();

  const UserAgreementPage = () => {
  const [agreementContent, setAgreementContent] = useState('');
  const [agreementTitle, setAgreementTitle] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pendingData, setPendingData] = useState(null);
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [agreement, setAgreement] = useState(null);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
  setLoading(true);
  setError('');
  console.log('[DEBUG] UserAgreementPage mounted.');

   try {
    // Check URL params first (for self-signup flow), then fall back to localStorage
    const urlParams = new URLSearchParams(window.location.search);
    const urlUserId = urlParams.get('userId');
    const urlAgreementId = urlParams.get('agreementId');
    const urlEmail = urlParams.get('email');
    
    let pendingAgreement;
    
    if (urlUserId && urlAgreementId) {
      // Self-signup flow: params came from email link
      pendingAgreement = {
        userId: urlUserId,
        agreementId: urlAgreementId,
        email: urlEmail || null
      };
      // Store in localStorage for consistency
      localStorage.setItem('pendingAgreement', JSON.stringify(pendingAgreement));
      console.log('[DEBUG] Loaded pending agreement from URL params:', pendingAgreement);
    } else {
      // Admin-created user flow: params in localStorage from login
      const pendingAgreementJSON = localStorage.getItem('pendingAgreement');
      if (!pendingAgreementJSON) {
        throw new Error('No pending agreement information found. Please try the login process again.');
      }
      pendingAgreement = JSON.parse(pendingAgreementJSON);
      console.log('[DEBUG] Found pending agreement in localStorage:', pendingAgreement);
    }
    console.log('[DEBUG] Found pending agreement in localStorage:', pendingAgreement);

    if (!pendingAgreement.agreementId || !pendingAgreement.userId) {
      throw new Error('Pending agreement data is incomplete.');
    }

    setPendingData(pendingAgreement);

    const fetchAgreementContent = async () => {
      try {
        const agreementRef = doc(db, 'userAgreements', pendingAgreement.agreementId);
        const docSnap = await getDoc(agreementRef);

        if (docSnap.exists()) {
  console.log('[DEBUG] Successfully fetched agreement document:', docSnap.data());
  const agreementData = docSnap.data();
  setAgreement(agreementData);
  setAgreementTitle(agreementData.title || 'License Agreement');
  setAgreementContent(agreementData.content || 'No content found.');
} else {
          throw new Error('Could not find the specified license agreement.');
        }
      } catch (fetchError) {
        console.error('Error fetching agreement document:', fetchError);
        setError(`Error loading agreement: ${fetchError.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchAgreementContent();

  } catch (e) {
    console.error('Error processing pending agreement:', e);
    setError(e.message);
    setLoading(false);
  }
}, []); // This effect should only run once when the component mounts

  const handleSubmit = async () => {
    if (!accepted) {
      setError('You must accept the terms to continue.');
      return;
    }

    if (!pendingData) {
      setError('Session data missing. Please restart the onboarding process.');
      return;
    }

    setError('');
    setSubmitting(true);

    try {
  const { userId, agreementId } = pendingData;

  const functions = getFunctions();
  const recordAgreementAcceptance = httpsCallable(functions, 'recordAgreementAcceptance');
  const result = await recordAgreementAcceptance({ userId, agreementId });

  if (result.data.success) {
    localStorage.removeItem('pendingAgreement');
    toast.success('Thank you for accepting the terms! Welcome to the platform.');
    
    // Refresh user data to get updated termsAcceptedAt
    await refreshUser();
    
    setTimeout(() => {
      console.log('[DEBUG] Acceptance recorded. Navigating to /');
      navigate('/');
    }, 2000);
  } else {
    throw new Error(result.data.message || 'Failed to record acceptance.');
  }
} catch (err) {
  console.error('Error submitting agreement acceptance:', err);
  let errorMessage = 'An error occurred while processing your acceptance. ';
  if (err.code === 'functions/not-found') {
    errorMessage += 'Service temporarily unavailable. Please try again.';
  } else if (err.code === 'functions/invalid-argument') {
    errorMessage += 'Invalid request data. Please restart the process.';
  } else if (err.message) {
    errorMessage += err.message;
  } else {
    errorMessage += 'Please try again or contact support.';
  }
  setError(errorMessage);
} finally {
  // This will run regardless of success or failure
  setSubmitting(false);
}
  };

  const handleCancel = () => {
    // Clear localStorage and redirect to login
    localStorage.removeItem('pendingAgreement');
    toast.info('Agreement process cancelled. Please contact support if you need assistance.');
    navigate('/login');
  };

  if (loading) {
    return (
      <Container component="main" maxWidth="md" sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Loading agreement...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {agreementTitle}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Please read and accept the following terms to activate your account.
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            height: '400px',
            overflowY: 'auto',
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            p: 3,
            mb: 3,
            backgroundColor: '#fafafa',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#c1c1c1',
              borderRadius: '4px',
            },
          }}
        >
          <Typography 
            component="div" 
            sx={{ 
              whiteSpace: 'pre-wrap', 
              wordWrap: 'break-word',
              lineHeight: 1.6,
              fontSize: '0.95rem'
            }}
          >
            {agreementContent}
          </Typography>
        </Box>

        <FormControlLabel
          control={
            <Checkbox 
              checked={accepted} 
              onChange={(e) => setAccepted(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Typography variant="body1">
              I have read and agree to the terms and conditions above.
            </Typography>
          }
          sx={{ mb: 3 }}
        />

        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Button
            variant="outlined"
            onClick={handleCancel}
            disabled={submitting}
            sx={{ 
              textTransform: 'none', 
              fontSize: '1rem',
              flex: { xs: 1, sm: 'none' }
            }}
          >
            Cancel
          </Button>
          
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={!accepted || submitting}
            sx={{ 
              textTransform: 'none', 
              fontSize: '1rem',
              flex: 1,
              minHeight: '48px'
            }}
          >
            {submitting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                <span>Processing...</span>
              </Box>
            ) : (
              'Accept and Continue'
            )}
          </Button>
        </Box>

        {pendingData?.email && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            Account: {pendingData.email}
          </Typography>
        )}
      </Paper>
    </Container>
  );
};

export default UserAgreementPage;
